/**
 * 渲染分页编辑器导航缩略图，通过可见性事件显式请求对应 frame 的资源
 * 依赖 Frame 预览事件和水印服务，资源收集与加载由 Canvas 的 rAF 队列负责
 */
import React, { useEffect } from 'react';
import type { Editor } from '@meitu/saas-editor-core';
import type { EventEmitter } from '@meitu/saas-editor-core/es/framework/services/events';
import { NAV_SCROLL_FRAME_EVENT } from '../../event-name';

/** 导航停止滚动并保持可见 150ms 后才请求资源，跳过快速掠过的页面 */
const PREVIEW_RESOURCE_DELAY_MS = 150;

/**
 * 在导航页项中挂载缩略图，跟随 frame 预览、水印状态及 DOM 可见性更新
 *
 * @param props - 编辑器实例、导航事件、稳定 frame ID、页面尺寸及尺寸更新回调
 * @returns 导航缩略图画布
 * 停止滚动后请求可见 frame 资源，离开可视区或卸载时取消待触发任务
 */
export const RenderStagePreview: React.FC<{
    editor: Editor;
    events: EventEmitter;
    originIndex: number;
    width: number;
    height: number;
    onSizeChange: (size: { width: number; height: number }) => void;
}> = React.memo(({ editor, events, originIndex, width, height, onSizeChange }) => {
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    // 导航只需缩略图分辨率，CSS 尺寸仍沿用画板坐标以匹配外层缩放
    const pixelWidth = Math.max(1, Math.min(400, Math.round(width)));
    const pixelHeight = Math.max(1, Math.round((height * pixelWidth) / Math.max(1, width)));

    useEffect(() => {
        let watermarkLoading = false;
        let visible = false;
        let disposed = false;
        let canLoadResources = false;
        let resourceTimer: ReturnType<typeof setTimeout> | null = null;
        const cancelResourceRequest = () => {
            canLoadResources = false;
            if (resourceTimer !== null) clearTimeout(resourceTimer);
            resourceTimer = null;
        };
        const drawPreview = (sourceCanvas?: HTMLCanvasElement) => {
            if (disposed || !visible) return;
            if (canLoadResources) editor.services.edgelessCanvas.requestFrameResources(originIndex);
            const frame = editor.services.frame.get(originIndex);
            const canvas = sourceCanvas ?? frame?.previewCanvas;
            const targetCanvas = canvasRef.current;
            const ctx = targetCanvas?.getContext('2d');

            if (!canvas || !targetCanvas || !ctx) return;

            if (width <= 0 || height <= 0) return;

            targetCanvas.width = pixelWidth;
            targetCanvas.height = pixelHeight;
            ctx.scale(pixelWidth / width, pixelHeight / height);
            ctx.clearRect(0, 0, width, height);
            ctx.drawImage(canvas, 0, 0, width, height);

            const { svipWatermark } = editor.services;
            svipWatermark.drawFrameWatermark(originIndex, ctx, 0, 0, width, height);
            if (
                canLoadResources &&
                svipWatermark.hasGlobalWatermark(originIndex) &&
                svipWatermark.getWatermarkImage() &&
                !svipWatermark.getWatermarkImageEl() &&
                !watermarkLoading
            ) {
                watermarkLoading = true;
                void svipWatermark.loadWatermarkImage().then((image) => {
                    watermarkLoading = false;
                    if (image) {
                        drawPreview(sourceCanvas);
                    }
                });
            }
        };

        const scheduleResourceRequest = () => {
            cancelResourceRequest();
            if (disposed || !visible) return;
            resourceTimer = setTimeout(() => {
                resourceTimer = null;
                if (disposed || !visible) return;
                canLoadResources = true;
                drawPreview();
                editor.services.frame.get(originIndex)?.renderPreview();
            }, PREVIEW_RESOURCE_DELAY_MS);
        };
        // 可见性只决定候选页；持续滚动会重置等待，预览完成事件也不能绕过该等待。
        const scrollHandler = events.on(NAV_SCROLL_FRAME_EVENT, scheduleResourceRequest);
        const observer = new IntersectionObserver((entries) => {
            const entry = entries[entries.length - 1];
            if (!entry || disposed) return;
            visible = entry.isIntersecting;
            scheduleResourceRequest();
            drawPreview();
        });
        if (canvasRef.current) observer.observe(canvasRef.current);

        const handler = editor.services.frame.onPreviewComplete(({ frameId, canvas }) => {
            if (!disposed && frameId === originIndex) {
                onSizeChange(editor.services.frame.getSize(originIndex));
                drawPreview(canvas);
            }
        });
        const watermarkHandler = editor.services.svipWatermark.onWatermark(() => {
            drawPreview();
        });

        return () => {
            disposed = true;
            cancelResourceRequest();
            events.off(scrollHandler);
            observer.disconnect();
            editor.services.events.off(handler);
            editor.services.events.off(watermarkHandler);
        };
    }, [editor, events, originIndex, width, height, pixelWidth, pixelHeight, onSizeChange]);

    return <canvas ref={canvasRef} width={pixelWidth} height={pixelHeight} style={{ width, height }}></canvas>;
});

RenderStagePreview.displayName = 'RenderStagePreview';
